/** True when the string is a calendar date only (`yyyy-MM-dd`), with no time component. */
export function isIsoDateOnlyString(value: string): boolean {
    return /^\d{4}-\d{2}-\d{2}$/.test(value.trim());
}

/**
 * Parses a value into a valid `Date`, or `null` when invalid / empty.
 * Supports ISO strings, unix epoch (seconds or ms), and `Date` instances.
 */
export function parseSafeDate(value: unknown): Date | null {
    if (value === undefined || value === null) {
        return null;
    }

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === "number" && Number.isFinite(value)) {
        const millis = Math.abs(value) < 1e12 ? value * 1000 : value;
        const parsed = new Date(millis);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed === "") {
            return null;
        }

        if (/^\d+$/.test(trimmed)) {
            const numeric = Number(trimmed);
            if (Number.isFinite(numeric)) {
                return parseSafeDate(numeric);
            }
        }

        if (isIsoDateOnlyString(trimmed)) {
            const isoDateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
            if (isoDateOnly === null) {
                return null;
            }
            const year = Number(isoDateOnly[1]);
            const month = Number(isoDateOnly[2]);
            const day = Number(isoDateOnly[3]);
            const local = new Date(year, month - 1, day);
            return Number.isNaN(local.getTime()) ? null : local;
        }

        const parsed = new Date(trimmed);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    return null;
}
